import { useEffect, useState } from "react";
import { ArrowRight, Undo2, Scissors, Wallet } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { fmtRp } from "@/components/AmountInput";
import { coaName } from "@/lib/masterI18n";
import { fl } from "@/lib/finI18n";

// Settlement rules (salary / BPJS / PPh 21 / PPh Unifikasi) with what is still unpaid up to the end of the month of `date`.
export function usePayables(on, clientId, date, excludeId, lang, nonce = 0) {
  const [data, setData] = useState(null);
  const month = (date || "").slice(0, 7);
  useEffect(() => {
    if (!on || !clientId || !/^\d{4}-\d{2}$/.test(month)) { setData(null); return undefined; }
    let off = false;
    const p = new URLSearchParams({ client_id: clientId, date: `${month}-01`, lang });
    if (excludeId) p.set("exclude_id", excludeId);
    api.get(`/payables/outstanding?${p}`).then(({ data: d }) => { if (!off) setData(d); }).catch(() => { if (!off) setData(null); });
    return () => { off = true; };
  }, [on, clientId, month, excludeId, lang, nonce]);
  return data;
}

export function ruleFor(l, pay, desc, withholding) {
  if (!pay || !l.coa_id) return null;
  const byAcc = pay.rules.find((r) => r.from_coa_ids.includes(l.coa_id));
  if (byAcc) return byAcc;
  if (withholding || !pay.text_coa_ids.includes(l.coa_id)) return null;
  const txt = `${desc || ""} ${l.ledger || ""} ${l.note || ""}`;
  return pay.text_order.map((k) => pay.rules.find((r) => r.rule === k)).find((r) => r?.text && new RegExp(r.text, "i").test(txt)) || null;
}

// Unpaid balance of a payable left for row `i` after the rows above it that already pay it.
const leftFor = (lines, r, i, rate) => r.outstanding - lines.slice(0, i)
  .reduce((s, l) => s + (l.dc === "dr" && l.coa_id === r.coa_id ? (Number(l.amount) || 0) * rate : 0), 0);

// New Dr rows booked to an expense that is already accrued go to its payable (the expense is not counted twice).
export function autoSettle(lines, pay, desc, withholding, rate) {
  if (!pay) return null;
  let changed = false;
  const out = lines.map((l, i) => {
    if (l.dc !== "dr" || l.keep || l.auto) return l;
    const r = ruleFor(l, pay, desc, withholding);
    if (!r || leftFor(lines, r, i, rate) <= 0.5) return l;
    changed = true;
    return { ...l, coa_id: r.coa_id, ledger: r.ledger, auto: { rule: r.rule, from_coa_id: l.coa_id, from_ledger: l.ledger || "" } };
  });
  return changed ? out : null;
}

function NoticeRow({ row, coa, lang, rate, onUndo, onApply, onSplit }) {
  const { i, l, r, kind, left } = row;
  const from = coa.find((c) => c.id === (kind === "auto" ? l.auto.from_coa_id : l.coa_id));
  const amt = (Number(l.amount) || 0) * rate;
  const over = kind === "auto" && amt > left + 1;
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs" data-testid={`payable-row-${i}`}>
      <span className="font-medium">{fl("psRow", lang)} {i + 1}:</span>
      <span className={kind === "auto" ? "line-through text-muted-foreground" : ""}>{from ? coaName(from, lang) : "—"}</span>
      <ArrowRight className="h-3 w-3" />
      <span className="font-semibold">{r.code} {r.label}</span>
      <span className="text-muted-foreground">· {fl("psOutstanding", lang)}: <b className="num" data-testid={`payable-left-${i}`}>{fmtRp(left, lang)}</b></span>
      {over && <span className="text-rose-600" data-testid={`payable-over-${i}`}>{fl("psOver", lang)}</span>}
      <span className="ml-auto flex gap-1">
        {over && <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={() => onSplit(i, left)} data-testid={`payable-split-${i}`}><Scissors className="h-3 w-3 mr-1" />{fl("psSplit", lang)}</Button>}
        {kind === "auto"
          ? <Button type="button" size="sm" variant="ghost" className="h-7 text-xs" onClick={() => onUndo(i)} data-testid={`payable-undo-${i}`}><Undo2 className="h-3 w-3 mr-1" />{fl("psUndo", lang)}</Button>
          : <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={() => onApply(i, r)} data-testid={`payable-apply-${i}`}>{fl("psApply", lang)}</Button>}
      </span>
    </div>
  );
}

export function PayableNotice({ lines, pay, desc, withholding, rate, coa, onLines, lang }) {
  if (!pay) return null;
  const rows = [];
  lines.forEach((l, i) => {
    if (l.dc !== "dr") return;
    const auto = l.auto && pay.rules.find((x) => x.rule === l.auto.rule && x.coa_id === l.coa_id);
    const r = auto || (l.keep === "loaded" ? ruleFor(l, pay, desc, withholding) : null);
    const left = r ? leftFor(lines, r, i, rate) : 0;
    if (auto || (r && left > 0.5)) rows.push({ i, l, r, left, kind: auto ? "auto" : "suggest" });
  });
  if (!rows.length) return null;
  const put = (i, patch) => onLines(lines.map((l, k) => (k === i ? { ...l, ...patch } : l)));
  const onUndo = (i) => put(i, { coa_id: lines[i].auto.from_coa_id, ledger: lines[i].auto.from_ledger, auto: undefined, keep: "user" });
  const onApply = (i, r) => put(i, { coa_id: r.coa_id, ledger: r.ledger, keep: undefined, auto: { rule: r.rule, from_coa_id: lines[i].coa_id, from_ledger: lines[i].ledger || "" } });
  const onSplit = (i, left) => {
    const l = lines[i];
    const rest = Math.round(((Number(l.amount) || 0) - left / rate) * 100) / 100;
    const keepRow = { ...l, coa_id: l.auto.from_coa_id, ledger: l.auto.from_ledger, amount: String(rest), auto: undefined, keep: "user" };
    onLines([...lines.slice(0, i), { ...l, amount: String(Math.round((left / rate) * 100) / 100) }, keepRow, ...lines.slice(i + 1)]);
  };
  return (
    <div className="rounded-md border border-emerald-300/60 bg-emerald-50/60 dark:bg-emerald-950/20 p-3 space-y-2" data-testid="payable-notice">
      <p className="flex items-center gap-1.5 text-sm font-semibold text-emerald-800 dark:text-emerald-300"><Wallet className="h-4 w-4" />{fl("psTitle", lang)}</p>
      <p className="text-[11px] text-muted-foreground">{fl("psWhy", lang)} ({fl("psAsOf", lang)} {pay.as_of})</p>
      {rows.map((row) => <NoticeRow key={row.i} row={row} coa={coa} lang={lang} rate={rate} onUndo={onUndo} onApply={onApply} onSplit={onSplit} />)}
    </div>
  );
}
